// ============================================================================
// Formulaire de qualification (gate des funnels)
//   Profilage du lead + logique de qualification pour la prise de RDV.
//   Le QCM DIFFÈRE selon le funnel (cf. QCM_STEPS) :
//     • vsl        → maturité IA · situation · taille entreprise
//     • masterclass→ maturité IA · outil principal · situation · taille équipe
//   Tout le monde accède au contenu ; seuls les profils qualifiés vont au bout
//   (RDV / confirmation). Les non-qualifiés voient un écran « hors cible ».
// ============================================================================

import type { FunnelKind } from "./funnel-store";

export type Option = { value: string; label: string };

// --- Étape Maturité IA (commune aux 2 funnels) -----------------------------
// « Où en êtes-vous avec l'IA aujourd'hui ? »
export const MATURITE_IA_OPTIONS: Option[] = [
  { value: "pas_vraiment", label: "Pas vraiment" },
  { value: "chat", label: "En chat uniquement" },
  { value: "assistants", label: "J’utilise des assistants IA" },
  { value: "agents", label: "J’ai des agents IA" },
];

// --- Étape Outil principal (funnel masterclass) ----------------------------
// « Quel outil IA utilisez-vous principalement ? »
// Copilot et Gemini sont deux options SÉPARÉES, pas une seule : c'est ce qui
// dit si le lead est sous Microsoft 365 ou sous Google Workspace — la
// distinction qui structure la démonstration en direct. On peut toujours
// regrouper les deux à l'analyse ; l'inverse est impossible.
export const OUTIL_PRINCIPAL_OPTIONS: Option[] = [
  { value: "chatgpt", label: "ChatGPT" },
  { value: "claude", label: "Claude" },
  { value: "copilot", label: "Microsoft Copilot" },
  { value: "gemini", label: "Google Gemini" },
  { value: "autre", label: "Autre" },
];

// --- Étape Situation (qualification) ---------------------------------------
// « Votre situation actuelle »
// Qualifiés : freelance, en poste (CDI).
// Disqualifiés : en recherche de mission/poste, étudiant.
export const STATUT_OPTIONS: Option[] = [
  { value: "freelance", label: "Freelance / Indépendant" },
  { value: "cdi", label: "En poste (CDI)" },
  { value: "recherche", label: "En recherche de mission / poste" },
  { value: "etudiant", label: "Étudiant" },
];

const DISQUALIFYING_STATUTS = new Set(["recherche", "etudiant"]);

// --- Étape Taille d'entreprise (funnel vsl) --------------------------------
// « Taille de votre entreprise »
export const TAILLE_ENTREPRISE_OPTIONS: Option[] = [
  { value: "moins_10", label: "Moins de 10" },
  { value: "10_50", label: "10 à 50" },
  { value: "50_250", label: "50 à 250" },
  { value: "plus_250", label: "Plus de 250" },
];

// --- Étape Taille d'équipe managée (funnel masterclass) --------------------
// « Combien de personnes managez-vous ? »
export const TAILLE_EQUIPE_OPTIONS: Option[] = [
  { value: "aucune", label: "Je ne manage pas encore" },
  { value: "1_5", label: "1 à 5" },
  { value: "6_15", label: "6 à 15" },
  { value: "plus_15", label: "Plus de 15" },
];

// --- Données du formulaire -------------------------------------------------
// Union des champs des 2 funnels : seuls ceux du QCM courant sont remplis.
export type FormData = {
  nomComplet: string;
  email: string;
  phone: string; // normalisé en E.164 (+33…)
  maturiteIA: string;
  statut: string;
  tailleEntreprise: string;
  outilPrincipal: string;
  tailleEquipe: string;
};

export const EMPTY_FORM: FormData = {
  nomComplet: "",
  email: "",
  phone: "",
  maturiteIA: "",
  statut: "",
  tailleEntreprise: "",
  outilPrincipal: "",
  tailleEquipe: "",
};

// --- Étapes du QCM (config partagée hook ↔ composant ↔ webhook) ------------
// Étape 0 = contact ; étapes 1..N = QCM ci-dessous (dans l'ordre affiché).
// `label` = intitulé court envoyé à n8n (metadata.qN_label).
export type QcmStep = {
  key: keyof FormData;
  label: string;
  question: string;
  options: Option[];
};

const MATURITE_STEP: QcmStep = {
  key: "maturiteIA",
  label: "Maturité IA",
  question: "Où en êtes-vous avec l’IA aujourd’hui ?",
  options: MATURITE_IA_OPTIONS,
};

const STATUT_STEP: QcmStep = {
  key: "statut",
  label: "Situation",
  question: "Votre situation actuelle",
  options: STATUT_OPTIONS,
};

// ⚠️ L'ORDRE fait foi : il détermine le mapping Q1, Q2, Q3… côté webhook n8n.
// Ne pas réordonner le funnel `vsl` sans adapter le workflow n8n existant.
export const QCM_STEPS: Record<FunnelKind, QcmStep[]> = {
  vsl: [
    MATURITE_STEP,
    STATUT_STEP,
    {
      key: "tailleEntreprise",
      label: "Taille entreprise",
      question: "Taille de votre entreprise",
      options: TAILLE_ENTREPRISE_OPTIONS,
    },
  ],
  // /meet : le formulaire n'est que le filet de la popup de sortie (« on vous
  // rappelle »). Même QCM que la VSL : c'est la même cible, déjà connue.
  meet: [
    MATURITE_STEP,
    STATUT_STEP,
    {
      key: "tailleEntreprise",
      label: "Taille entreprise",
      question: "Taille de votre entreprise",
      options: TAILLE_ENTREPRISE_OPTIONS,
    },
  ],
  masterclass: [
    MATURITE_STEP,
    {
      key: "outilPrincipal",
      label: "Outil principal",
      question: "Quel outil IA utilisez-vous principalement ?",
      options: OUTIL_PRINCIPAL_OPTIONS,
    },
    STATUT_STEP,
    {
      key: "tailleEquipe",
      label: "Taille équipe managée",
      question: "Combien de personnes managez-vous ?",
      options: TAILLE_EQUIPE_OPTIONS,
    },
  ],
};

// --- Copie de l'étape contact (varie selon le funnel) ----------------------
// L'étape 0 (contact) ne promet pas la même chose selon la LP : accès à la
// vidéo côté `vsl`, place à la session live côté `masterclass`.
export type GateCopy = {
  title: string;
  subtitle: string;
  phoneHint: string; // raison d'être du numéro — elle diffère par funnel
  cta: string;
  legal: string;
};

export const GATE_COPY: Record<FunnelKind, GateCopy> = {
  vsl: {
    title: "Votre accès + votre ressource",
    subtitle:
      "30 s. On vous ouvre la vidéo tout de suite, et on vous envoie votre ressource (copie de la vidéo + note de synthèse) par message et par email.",
    phoneHint: "pour recevoir votre ressource par message",
    cta: "Continuer",
    legal:
      "Accès immédiat à la vidéo. On vous envoie votre ressource, c’est tout — pas de spam.",
  },
  // Popup de sortie de /meet : pas de créneau pris → on rappelle.
  meet: {
    title: "Pas le temps de choisir un créneau ?",
    subtitle:
      "Laissez vos coordonnées : un expert vous rappelle pour caler votre point IA offert au moment qui vous arrange.",
    phoneHint: "pour que l’expert vous rappelle",
    cta: "Continuer",
    legal: "Un appel pour caler votre point, c’est tout — pas de spam.",
  },
  // ⚠️ COPIE À VALIDER — en attente du contenu masterclass.
  masterclass: {
    title: "Réservez votre place",
    subtitle:
      "30 s. On vous envoie le lien de connexion et un rappel avant le début, par message et par email.",
    phoneHint: "pour le lien de connexion et le rappel avant le direct",
    cta: "Continuer",
    legal:
      "Votre place est confirmée à la fin du formulaire. Lien de connexion + rappel — pas de spam.",
  },
};

/** Nombre total d'étapes du funnel : contact + QCM. */
export const totalSteps = (funnel: FunnelKind) => 1 + QCM_STEPS[funnel].length;

// --- Logique de qualification ----------------------------------------------
// Identique aux 2 funnels pour l'instant : seule la situation disqualifie.
export function isQualified(data: Pick<FormData, "statut">): boolean {
  return data.statut !== "" && !DISQUALIFYING_STATUTS.has(data.statut);
}

// --- Prénom (personnalisation VSL / Thank You) -----------------------------
export const firstNameOf = (nomComplet: string) =>
  nomComplet.trim().split(/\s+/)[0] ?? "";

// --- Validations -----------------------------------------------------------
export const isValidEmail = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());
// phone normalisé (+33…) ou local : on valide sur le nombre de chiffres
export const isValidPhone = (v: string) => /^\+?\d{8,15}$/.test(v.replace(/[\s.\-()]/g, ""));
